/* ==========================================================================
   theme-a11y.js — keyboard and screen-reader behaviour for the theme's
   dialogs and accordions.

   Written as a separate module so theme.js stays untouched. It watches the
   dialogs' `open` class with a MutationObserver, so it reacts no matter which
   code path opened or closed them.
   ========================================================================== */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function focusableIn(el) {
    return Array.prototype.filter.call(el.querySelectorAll(FOCUSABLE), function (node) {
      var r = node.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });
  }

  /* ---------- One dialog: focus in on open, focus back on close, trap Tab --- */
  function manageDialog(el, closeFn) {
    if (!el) return;
    var lastFocused = null;
    var isOpen = el.classList.contains('open');

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeFn();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = focusableIn(el);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !el.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !el.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    }

    // The dialog fades in, so the first focusable element can still be
    // zero-sized for a frame or two. Keep trying briefly rather than giving up.
    function focusFirst(attempt) {
      if (!el.classList.contains('open')) return;
      var items = focusableIn(el);
      if (items.length) {
        items[0].focus();
        return;
      }
      if (attempt < 12) {
        setTimeout(function () { focusFirst(attempt + 1); }, 60);
      } else {
        el.setAttribute('tabindex', '-1');
        el.focus();
      }
    }

    function onOpen() {
      lastFocused = document.activeElement;
      el.setAttribute('aria-modal', 'true');
      el.removeAttribute('aria-hidden');
      document.addEventListener('keydown', onKeydown, true);
      focusFirst(0);
    }

    function onClose() {
      el.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', onKeydown, true);
      if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
      lastFocused = null;
    }

    if (!isOpen) el.setAttribute('aria-hidden', 'true');

    new MutationObserver(function () {
      var nowOpen = el.classList.contains('open');
      if (nowOpen === isOpen) return;
      isOpen = nowOpen;
      if (nowOpen) { onOpen(); } else { onClose(); }
    }).observe(el, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------- Accordions: expose open/closed state -------------------------- */
  function wireAccordions() {
    document.querySelectorAll('.acc-head').forEach(function (head, i) {
      var item = head.closest('.acc-item');
      if (!item) return;
      var panel = item.querySelector('.acc-body');
      if (panel) {
        if (!panel.id) panel.id = 'acc-panel-' + i;
        head.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-labelledby', head.id || (head.id = 'acc-head-' + i));
      }
      function sync() {
        head.setAttribute('aria-expanded', item.classList.contains('open') ? 'true' : 'false');
      }
      sync();
      new MutationObserver(sync).observe(item, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* ---------- Decorative icons should not be announced ---------------------- */
  function hideDecorativeIcons() {
    document.querySelectorAll('svg:not([aria-hidden]):not([aria-label])').forEach(function (svg) {
      if (svg.querySelector('title')) return;
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
    });
  }

  /* ---------- Cart bubble: hide it while the cart is empty ------------------
     theme.js writes the count into these elements as plain text, so rather
     than patching that code the badge watches its own text and hides itself
     at zero. An orange "0" on the cart icon is noise, and it also reads out
     as "0 items" to a screen reader on every page.                            */
  function syncCartBadges() {
    document.querySelectorAll('.cart-count, .mbn-cart-count').forEach(function (badge) {
      function sync() {
        var n = parseInt((badge.textContent || '').trim(), 10);
        badge.style.display = (!n || n < 1) ? 'none' : '';
      }
      sync();
      new MutationObserver(sync).observe(badge, { childList: true, characterData: true, subtree: true });
    });
  }

  function init() {
    manageDialog(document.getElementById('qvModal'), function () {
      if (window.QuickView && window.QuickView.close) window.QuickView.close();
    });
    manageDialog(document.getElementById('cartDrawer'), function () {
      if (window.LarkvilleCart && window.LarkvilleCart.close) window.LarkvilleCart.close();
    });
    wireAccordions();
    hideDecorativeIcons();
    syncCartBadges();

    // The cart drawer re-renders its contents, so re-hide any new icons.
    var items = document.getElementById('cartItems');
    if (items) new MutationObserver(hideDecorativeIcons).observe(items, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
